import React, { useEffect, useState, useCallback, useRef, Suspense } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import './ShowcaseShell.css';

const EXIT_MS = 260;

const ALL_PROJECTS = [
  {
    id:     'sentinel',
    label:  'Sentinel',
    path:   '/projects/sentinel',
    tag:    'ML Pipeline',
    github: 'https://github.com/adsouza5/sentinel-ml-pipeline',
    live:   true,
  },
  {
    id:     'prism',
    label:  'Prism',
    path:   '/projects/prism',
    tag:    'API Gateway',
    github: 'https://github.com/adsouza5/prism-api',
    live:   true,
  },
  {
    id:     'lens',
    label:  'Lens',
    path:   '/projects/lens',
    tag:    'Semantic Search',
    github: 'https://github.com/adsouza5/lens-api',
    live:   true,
  },
  {
    id:     'codecollab',
    label:  'CodeCollab',
    path:   '/projects/codecollab',
    tag:    'Collab Editor',
    github: 'https://github.com/adsouza5/RealTimeCoding',
    live:   false,
  },
  {
    id:     'flux',
    label:  'Flux',
    path:   '/projects/flux',
    tag:    'Unit Converter',
    github: 'https://github.com/adsouza5/currency-convert-chatbot',
    live:   false,
  },
];

const YEAR_PROJECTS = {
  '2026': ['sentinel', 'prism', 'lens'],
  '2024': ['flux'],
  '2023': ['codecollab'],
};

export default function ShowcaseShell() {
  const navigate = useNavigate();
  const location = useLocation();

  const [fading,    setFading]    = useState(false);
  const [renderKey, setRenderKey] = useState(location.pathname);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const [year,      setYear]      = useState(location.state?.year ?? null);

  const tabRefs = useRef([]);

  // Persist the year across same-session project switches —
  // once set by galaxy navigation, keep it until the user leaves the showcase.
  useEffect(() => {
    if (location.state?.year) setYear(location.state.year);
  }, [location.state?.year]);

  const PROJECTS = year && YEAR_PROJECTS[year]
    ? ALL_PROJECTS.filter(p => YEAR_PROJECTS[year].includes(p.id))
    : ALL_PROJECTS;

  const idx     = PROJECTS.findIndex(p => location.pathname.startsWith(p.path));
  const current = PROJECTS[idx] ?? null;
  const prev    = idx > 0 ? PROJECTS[idx - 1] : null;
  const next    = idx < PROJECTS.length - 1 ? PROJECTS[idx + 1] : null;

  useEffect(() => {
    document.body.classList.add('in-showcase');
    return () => document.body.classList.remove('in-showcase');
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setFading(false);
    setRenderKey(location.pathname);
  }, [location.pathname]);

  // Slide indicator to active tab
  useEffect(() => {
    const el = tabRefs.current[idx];
    if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
  }, [idx, PROJECTS.length]);

  const navTo = useCallback((path) => {
    if (fading || location.pathname === path) return;
    setFading(true);
    const dest = year ? { pathname: path, state: { year } } : path;
    setTimeout(() => navigate(dest), EXIT_MS);
  }, [fading, location.pathname, navigate, year]);

  const goBack = useCallback(() => {
    if (year) {
      navigate('/', { state: { openGalaxy: true } });
    } else {
      navigate('/', { state: { scrollTo: 'projects' } });
    }
  }, [navigate, year]);

  if (!current) return <Outlet />;

  return (
    <div className="shell-wrap">

      <nav className="shell-topbar" aria-label="Showcase navigation">

        <button
          className="shell-back-btn"
          onClick={goBack}
          aria-label={year ? 'Return to galaxy' : 'Back to timeline'}
        >
        <svg width="11" height="10" viewBox="0 0 11 10" fill="none" aria-hidden="true">
            <path d="M3.5 1L1 5L3.5 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7.5 1L10 5L7.5 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>{year ? 'Galaxy' : 'Timeline'}</span>
        </button>

        <div className="shell-sep" aria-hidden="true" />

        <div className="shell-identity">
          <span className="shell-identity-name">{current.label}</span>
          <span className="shell-identity-tag">{current.tag}</span>
          {current.live && (
            <span className="shell-live">
              <span className="shell-live-dot" aria-hidden="true" />
              live
            </span>
          )}
        </div>

        {/* ── Right side: pill (galaxy-only) + GitHub ── */}
        <div className="shell-right">

          {year && (
            <div className="shell-pill" role="list" aria-label="Project switcher">
              <span className="shell-pill-year">{year}</span>
              <span className="shell-pill-divider" aria-hidden="true" />
              <span
                className="shell-pill-indicator"
                style={{ left: indicator.left, width: indicator.width }}
                aria-hidden="true"
              />
              {PROJECTS.map((p, i) => (
                <button
                  key={p.id}
                  ref={el => (tabRefs.current[i] = el)}
                  role="listitem"
                  className={`shell-pill-tab${i === idx ? ' shell-pill-tab--active' : ''}`}
                  onClick={() => navTo(p.path)}
                  aria-label={`${p.label} showcase`}
                  aria-current={i === idx ? 'page' : undefined}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}

          <a
            className="shell-gh"
            href={current.github}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${current.label} on GitHub`}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483
                0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466
                -.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832
                .092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688
                -.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004
                1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7
                1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855
                0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484
                17.522 2 12 2z" />
            </svg>
            <span className="shell-gh-label">GitHub</span>
          </a>

        </div>
      </nav>

      <div
        key={renderKey}
        className={`shell-page${fading ? ' shell-page--out' : ''}`}
      >
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
      </div>

      <div className="shell-bottombar" role="navigation" aria-label="Project pagination">
        <div className="shell-bottom-inner">

          <button
            className="shell-nav-btn shell-nav-btn--prev"
            onClick={() => prev && navTo(prev.path)}
            disabled={!prev}
            aria-label={prev ? `Previous: ${prev.label}` : 'No previous project'}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M10 3L5.5 8 10 13" stroke="currentColor" strokeWidth="1.6"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <div className="shell-counter" aria-label={`Project ${idx + 1} of ${PROJECTS.length}`}>
            <span key={`${year}-${idx}`} className="shell-counter-num">{idx + 1}</span>
            <span className="shell-counter-denom">/ {PROJECTS.length}</span>
          </div>

          <button
            className="shell-nav-btn shell-nav-btn--next"
            onClick={() => next && navTo(next.path)}
            disabled={!next}
            aria-label={next ? `Next: ${next.label}` : 'No next project'}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M6 3l4.5 5L6 13" stroke="currentColor" strokeWidth="1.6"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

        </div>
      </div>

    </div>
  );
}
